// src/pages/SettingsTeam.jsx

import React, { useState, useEffect, useMemo } from 'react';
import {
  UserPlus,
  MoreVertical,
  Mail,
  X,
  Loader2,
  AlertCircle,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import { apiCall } from '../utils/api';

const ASSIGNABLE_ROLES = ['admin', 'analyst', 'viewer'];

export default function SettingsTeam() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Inline-action state (for small feedback on a specific row)
  const [busyRow, setBusyRow] = useState(null);

  // --- Load --------------------------------------------------------------

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const meRes = await apiCall('/users/me');
      if (meRes.ok) {
        const me = await meRes.json();
        setCurrentUserEmail(me.email);
        setIsAdmin((me.roles || []).includes('admin'));
      }

      const [usersRes, invitesRes] = await Promise.all([
        apiCall('/users/'),
        apiCall('/invitations/'),
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (invitesRes.ok) {
        const data = await invitesRes.json();
        setInvitations(data.invitations || []);
      } else if (invitesRes.status === 403) {
        // Non-admins can't list invitations; that's fine
        setInvitations([]);
      }
    } catch (err) {
      console.error('Failed to load team:', err);
      setError('Could not load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // --- Actions -----------------------------------------------------------

  const changeRole = async (userId, newRole) => {
    setBusyRow(`user-${userId}`);
    try {
      const res = await apiCall(`/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Could not change role');
      }
      await load();
    } finally {
      setBusyRow(null);
    }
  };

  const deactivateUser = async (userId, email) => {
    if (!confirm(`Remove ${email} from the team? Their account will be deactivated and their seat will be freed.`)) return;
    setBusyRow(`user-${userId}`);
    try {
      const res = await apiCall(`/users/${userId}/deactivate`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Could not deactivate user');
      }
      await load();
    } finally {
      setBusyRow(null);
    }
  };

  const revokeInvitation = async (invitationId, email) => {
    if (!confirm(`Revoke invitation to ${email}?`)) return;
    setBusyRow(`invite-${invitationId}`);
    try {
      const res = await apiCall(`/invitations/${invitationId}/revoke`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Could not revoke invitation');
      }
      await load();
    } finally {
      setBusyRow(null);
    }
  };

  // --- Rendering ---------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-white">Team</h2>
          <p className="text-sm text-[#a0a0a0] mt-1">
            {users.length} active {users.length === 1 ? 'member' : 'members'}
            {invitations.length > 0 && `, ${invitations.length} pending`}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowInviteModal(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite member
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Members table */}
      <Card>
        <CardContent className="p-0">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={`user-${user.id}`}>
                    <TableCell className="font-medium text-white">
                      {user.email}
                      {user.email === currentUserEmail && (
                        <span className="ml-2 text-xs text-[#a0a0a0]">(you)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isAdmin && user.email !== currentUserEmail ? (
                        <select
                          value={user.role || ''}
                          disabled={busyRow === `user-${user.id}`}
                          onChange={e => changeRole(user.id, e.target.value)}
                          className="bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-white"
                        >
                          {ASSIGNABLE_ROLES.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      ) : (
                        <Badge variant="secondary">{user.role || '—'}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default" className="bg-green-500/20 text-green-400 border-green-500/30">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && user.email !== currentUserEmail && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyRow === `user-${user.id}`}
                          onClick={() => deactivateUser(user.id, user.email)}
                        >
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {invitations.map(inv => (
                  <TableRow key={`invite-${inv.id}`}>
                    <TableCell className="text-[#a0a0a0]">
                      <span className="inline-flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5" />
                        {inv.email}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{inv.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="default" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                        Pending
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyRow === `invite-${inv.id}`}
                          onClick={() => revokeInvitation(inv.id, inv.email)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {users.length === 0 && invitations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-[#a0a0a0] py-8">
                      No team members yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        </CardContent>
      </Card>

      {/* Invite modal */}
      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onInvited={() => { setShowInviteModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Invite Modal ─────────────────────────────────────────────────────────

function InviteModal({ onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await apiCall('/invitations/', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), role }),
      });
      if (res.ok) {
        onInvited();
      } else {
        const err = await res.json().catch(() => ({}));
        // err.detail may be a string or a LimitExceeded dict
        if (typeof err.detail === 'object' && err.detail?.message) {
          setError(err.detail.message);
        } else {
          setError(err.detail || 'Could not send invitation.');
        }
      }
    } catch (err) {
      console.error('Invite error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Invite a new member</h3>
          <button
            onClick={onClose}
            className="text-[#a0a0a0] hover:text-white"
            disabled={submitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
              autoFocus
            />
          </div>

          <div>
            <Label>Role</Label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-white"
            >
              {ASSIGNABLE_ROLES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !email.trim()}>
              {submitting ? 'Sending...' : 'Send invitation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}